"use client";

import { memo, useCallback, useEffect, useState } from "react";
import * as Collapsible from "@radix-ui/react-collapsible";
import { ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { usePdfDocumentStore } from "./pdfDocumentStore";
import { usePdfViewerContext } from "./PdfViewerCore";
import type { OutlineNode } from "./types";
import { ClientLogger } from "@/lib/clientLogger";

export const PdfOutlineTree = memo(function PdfOutlineTree() {
  const { t } = useTranslation();
  const activeDocumentId = usePdfDocumentStore((s) => s.activeDocumentId);
  const documents = usePdfDocumentStore((s) => s.documents);
  const activeDoc = documents.find((d) => d.id === activeDocumentId);

  const [outline, setOutline] = useState<OutlineNode[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeDoc) {
      setOutline(null);
      return;
    }
    if (!activeDoc.pdfDocumentProxy) {
      return;
    }
    setLoading(true);
    activeDoc.pdfDocumentProxy
      .getOutline()
      .then((data) => setOutline((data as OutlineNode[] | null) ?? []))
      .catch((err) => {
        ClientLogger.debug("PDF outline fetch failed", {
          error: err, component: "PdfOutlineTree",
        });
        setOutline([]);
      })
      .finally(() => setLoading(false));
  }, [activeDoc]);

  if (loading) {
    return (
      <div className="p-3 text-xs text-muted-foreground">{t("common.loading")}</div>
    );
  }

  if (!outline || outline.length === 0) {
    return (
      <div className="p-3 text-xs text-muted-foreground">
        {t("pdf.outlineEmpty")}
      </div>
    );
  }

  return (
    <div className="overflow-y-auto p-1" style={{ scrollbarWidth: "thin" }}>
      {outline.map((node, i) => (
        <OutlineItem key={`${node.title}-${i}`} node={node} depth={0} />
      ))}
    </div>
  );
});

const OutlineItem = memo(function OutlineItem({
  node,
  depth,
}: {
  node: OutlineNode;
  depth: number;
}) {
  const { instanceRef } = usePdfViewerContext();
  const hasChildren = node.items && node.items.length > 0;

  const navigate = useCallback(() => {
    const ls = instanceRef.current?.linkService;
    if (ls && node.dest) {
      ls.goToDestination(node.dest);
    }
  }, [instanceRef, node.dest]);

  if (!hasChildren) {
    return (
      <button
        type="button"
        onClick={navigate}
        className={cn(
          "flex w-full items-center rounded-sm px-2 py-1 text-left text-xs text-foreground/80 transition-colors hover:bg-muted/60",
          node.bold && "font-semibold",
          node.italic && "italic",
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
      >
        <span className="truncate">{node.title}</span>
      </button>
    );
  }

  return (
    <Collapsible.Root defaultOpen={depth < 1}>
      <Collapsible.Trigger asChild>
        <button
          type="button"
          onClick={navigate}
          className={cn(
            "group flex w-full items-center gap-1 rounded-sm px-2 py-1 text-left text-xs text-foreground/80 transition-colors hover:bg-muted/60",
            node.bold && "font-semibold",
            node.italic && "italic",
          )}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
        >
          <ChevronRightIcon className="size-3 shrink-0 transition-transform group-data-[state=open]:rotate-90" />
          <span className="truncate">{node.title}</span>
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        {node.items.map((child, i) => (
          <OutlineItem
            key={`${child.title}-${i}`}
            node={child}
            depth={depth + 1}
          />
        ))}
      </Collapsible.Content>
    </Collapsible.Root>
  );
});
